<template>
  <form ref="form" class="form-forgot-password-v2" @submit="submit">
    <app-overlay :show="busy" variant="dark">
      <template v-if="!requestSent">
        <div class="row g-2">
          <div class="email-field col-12">
            <input v-model="email" type="email" class="form-control" :placeholder="t('Email')" required />
          </div>

          <div v-if="showUsername" class="username-field col-12">
            <input v-model="username" type="text" class="form-control" :placeholder="t('Username')" required />
          </div>

          <div v-if="error" class="invalid-feedback d-block text-center">
            {{ error }}
          </div>
        </div>

        <p class="mt-3 mb-0 text-center forgot-notice">
          {{ t("If you have any difficulty restoring your password, please contact us at technikfee@ersties.com") }}
        </p>

        <button class="btn btn-primary border-dark w-100 mt-2" type="submit">
          {{ t("Send") }}
        </button>
      </template>

      <div v-else>
        {{
          t(
            "We have sent password reset instructions to your [sentEmail] email address. If you don't receive instructions within a few minutes, check your email's spam and junk filters. If the instruction aren't there, please contact our technical support.",
          ).replace("[sentEmail]", email)
        }}
      </div>
    </app-overlay>
  </form>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import AppOverlay from "@/components/v2/app-overlay";
import AuthService from "@/api/services/authService";
import { AUTH_ERROR_MULTIPLE_USERS } from "@/utils/variables.ts";

defineOptions({
  name: "form-forgot-password",
});

const { t } = useI18n();
const showUsername = ref(false);
const username = ref(null);
const email = ref("");
const busy = ref(false);
const requestSent = ref(false);
const error = ref("");
const form = ref<HTMLElement | null>(null);

const submit = async (e: Event) => {
  if (busy.value || requestSent.value) {
    return;
  }

  e.preventDefault();
  const formElement = e.target as HTMLFormElement;

  if (!formElement.checkValidity()) {
    formElement.reportValidity();
    return;
  }

  busy.value = true;

  try {
    await AuthService.forgot_password(email.value, username.value);
    requestSent.value = true;
  } catch (e: any) {
    const status = e.status?.status;

    if (status === 401) {
      if (e.status?.message === AUTH_ERROR_MULTIPLE_USERS) {
        showUsername.value = true;
        error.value = t("Please enter your username to help us verify that it’s you");
      } else {
        error.value = t("Please check your details.");
      }
    } else {
      error.value = t("Something went wrong");
    }
  } finally {
    busy.value = false;
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
